/**
 * Conditions Portal Service
 *
 * Manages secure token-based access to the conditions portal where
 * recipients can view loan conditions, download approval PDFs,
 * access condition template forms, and upload documents.
 *
 * Portal is accessed by processors, loan officers, borrowers, or agents.
 *
 * Security: 256-bit tokens (64 hex chars), dual expiration (90 days OR loan funded),
 * rate limiting, view logging, and manual revocation support.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import {
  tokenRowToToken,
  uploadRowToUpload,
  detectDeviceType,
  TOKEN_SETTINGS,
  FILE_LIMITS,
  type ConditionsPortalToken,
  type ConditionsPortalTokenRow,
  type ConditionsPortalViewEvent,
  type ConditionsPortalUpload,
  type ConditionsPortalUploadRow,
  type RecipientType,
  type DeviceType
} from '@/types/conditions-portal';

const log = createComponentLogger('ConditionsPortalService', 'service');

/**
 * Portal URL configuration
 * Uses VITE_APP_URL if set (for production), otherwise falls back to current origin
 * This ensures the portal URL always points to the public app domain
 */
const PORTAL_BASE_URL = (() => {
  const appUrl = import.meta.env.VITE_APP_URL;
  if (appUrl) {
    return `${appUrl}/conditions`;
  }
  return typeof window !== 'undefined'
    ? `${window.location.origin}/conditions`
    : '/conditions';
})();

export class ConditionsPortalService {
  /**
   * Generate a cryptographically secure 64-character hex token (256-bit entropy)
   * This provides brute-force infeasibility with 2^256 possible combinations
   */
  private static generateSecureToken(): string {
    const array = new Uint8Array(32); // 32 bytes = 256 bits
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Calculate expiration date (90 days from now by default)
   */
  private static calculateExpiration(days: number = TOKEN_SETTINGS.defaultExpirationDays): Date {
    const now = new Date();
    return new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  }

  /**
   * Create a new portal access token for a loan recipient
   * Returns the token record and the full portal URL
   */
  static async createPortalToken(params: {
    encompassLoanId: string;
    loanNumber?: string;
    recipientEmail: string;
    recipientName: string;
    recipientType?: RecipientType;
    createdByUserId?: string;
    expirationDays?: number;
  }): Promise<{ token: ConditionsPortalToken; portalUrl: string } | null> {
    try {
      log.info('Creating conditions portal token', {
        encompassLoanId: params.encompassLoanId,
        loanNumber: params.loanNumber,
        recipientEmail: params.recipientEmail,
        recipientType: params.recipientType || 'borrower'
      });

      const token = this.generateSecureToken();
      const expiresAt = this.calculateExpiration(params.expirationDays);

      const insertData = {
        encompass_loan_id: params.encompassLoanId,
        loan_number: params.loanNumber || null,
        token,
        recipient_email: params.recipientEmail.toLowerCase(),
        recipient_name: params.recipientName,
        recipient_type: params.recipientType || 'borrower',
        created_by_user_id: params.createdByUserId || null,
        expires_at: expiresAt.toISOString(),
        is_active: true
      };

      const { data, error } = await supabase
        .from('conditions_portal_tokens')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        log.error('Failed to create portal token', {
          encompassLoanId: params.encompassLoanId,
          error: error.message
        });
        return null;
      }

      const tokenRecord = tokenRowToToken(data as ConditionsPortalTokenRow);
      const portalUrl = `${PORTAL_BASE_URL}/${token}`;

      log.info('Portal token created successfully', {
        tokenId: tokenRecord.id,
        encompassLoanId: params.encompassLoanId,
        expiresAt: tokenRecord.expiresAt
      });

      return { token: tokenRecord, portalUrl };
    } catch (error) {
      log.error('Error creating portal token', {
        encompassLoanId: params.encompassLoanId,
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }

  /**
   * Get an active (non-expired, non-revoked) token for a loan
   * Returns null if no active token exists
   */
  static async getActiveTokenForLoan(encompassLoanId: string): Promise<ConditionsPortalToken | null> {
    try {
      log.debug('Checking for active portal token', { encompassLoanId });

      const { data, error } = await supabase
        .from('conditions_portal_tokens')
        .select('*')
        .eq('encompass_loan_id', encompassLoanId)
        .eq('is_active', true)
        .is('revoked_at', null)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !data) {
        log.debug('No active portal token found', { encompassLoanId });
        return null;
      }

      const tokenRecord = tokenRowToToken(data as ConditionsPortalTokenRow);
      log.info('Found active portal token', {
        tokenId: tokenRecord.id,
        encompassLoanId,
        expiresAt: tokenRecord.expiresAt
      });

      return tokenRecord;
    } catch (error) {
      log.warn('Error checking for active portal token', {
        encompassLoanId,
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }

  /**
   * Get active tokens for a loan, separated by type:
   * - loProcessor: The primary portal token (borrower, co_borrower, or partner types)
   * - titleEscrow: The title/escrow specific token
   *
   * This allows the UI to show both portal URLs when they exist.
   */
  static async getActiveTokensByType(encompassLoanId: string): Promise<{
    loProcessor: ConditionsPortalToken | null;
    titleEscrow: ConditionsPortalToken | null;
  }> {
    try {
      log.debug('Fetching active portal tokens by type', { encompassLoanId });

      const now = new Date().toISOString();

      // Fetch all active, non-expired tokens for this loan
      const { data, error } = await supabase
        .from('conditions_portal_tokens')
        .select('*')
        .eq('encompass_loan_id', encompassLoanId)
        .eq('is_active', true)
        .is('revoked_at', null)
        .gt('expires_at', now)
        .order('created_at', { ascending: false });

      if (error || !data || data.length === 0) {
        log.debug('No active portal tokens found', { encompassLoanId });
        return { loProcessor: null, titleEscrow: null };
      }

      // Find the most recent token of each type
      let loProcessorToken: ConditionsPortalToken | null = null;
      let titleEscrowToken: ConditionsPortalToken | null = null;

      for (const row of data) {
        const token = tokenRowToToken(row as ConditionsPortalTokenRow);

        if (token.recipientType === 'title_escrow') {
          // Take the most recent title_escrow token (first match since ordered by created_at DESC)
          if (!titleEscrowToken) {
            titleEscrowToken = token;
          }
        } else {
          // Take the most recent non-title_escrow token (borrower, co_borrower, or partner)
          if (!loProcessorToken) {
            loProcessorToken = token;
          }
        }

        // Exit early if we found both
        if (loProcessorToken && titleEscrowToken) {
          break;
        }
      }

      log.info('Found active portal tokens', {
        encompassLoanId,
        hasLoProcessor: !!loProcessorToken,
        hasTitleEscrow: !!titleEscrowToken
      });

      return { loProcessor: loProcessorToken, titleEscrow: titleEscrowToken };
    } catch (error) {
      log.warn('Error fetching active portal tokens by type', {
        encompassLoanId,
        error: error instanceof Error ? error.message : String(error)
      });
      return { loProcessor: null, titleEscrow: null };
    }
  }

  /**
   * Validate a portal token and return the token record if valid
   * This is a lightweight check - full portal data is fetched by the edge function
   */
  static async validateToken(token: string): Promise<{
    valid: boolean;
    tokenRecord?: ConditionsPortalToken;
    errorCode?: 'INVALID_TOKEN' | 'EXPIRED_TOKEN' | 'REVOKED_TOKEN';
  }> {
    try {
      log.debug('Validating portal token', { tokenPrefix: token.substring(0, 8) });

      const { data, error } = await supabase
        .from('conditions_portal_tokens')
        .select('*')
        .eq('token', token)
        .single();

      if (error || !data) {
        log.warn('Portal token not found', { tokenPrefix: token.substring(0, 8) });
        return { valid: false, errorCode: 'INVALID_TOKEN' };
      }

      const tokenRecord = tokenRowToToken(data as ConditionsPortalTokenRow);

      // Check if revoked
      if (tokenRecord.revokedAt || !tokenRecord.isActive) {
        log.warn('Portal token is revoked', {
          tokenId: tokenRecord.id,
          revokedAt: tokenRecord.revokedAt
        });
        return { valid: false, errorCode: 'REVOKED_TOKEN', tokenRecord };
      }

      // Check expiration
      const now = new Date();
      const expiresAt = new Date(tokenRecord.expiresAt);
      if (expiresAt < now) {
        log.warn('Portal token is expired', {
          tokenId: tokenRecord.id,
          expiresAt: tokenRecord.expiresAt
        });
        return { valid: false, errorCode: 'EXPIRED_TOKEN', tokenRecord };
      }

      log.info('Portal token validated successfully', {
        tokenId: tokenRecord.id,
        encompassLoanId: tokenRecord.encompassLoanId
      });

      return { valid: true, tokenRecord };
    } catch (error) {
      log.error('Error validating portal token', {
        tokenPrefix: token.substring(0, 8),
        error: error instanceof Error ? error.message : String(error)
      });
      return { valid: false, errorCode: 'INVALID_TOKEN' };
    }
  }

  /**
   * Track a view of the portal
   * Updates view stats and creates a view event for audit trail
   * Only tracks successful views for active, non-expired tokens
   */
  static async trackView(params: {
    token: string;
    ipAddress?: string;
    userAgent?: string;
    success?: boolean;
    failureReason?: string;
  }): Promise<boolean> {
    try {
      log.info('Tracking portal view', { tokenPrefix: params.token.substring(0, 8) });

      // First, get the token record with status fields
      const { data: tokenData, error: tokenError } = await supabase
        .from('conditions_portal_tokens')
        .select('id, first_viewed_at, view_count, is_active, expires_at, revoked_at')
        .eq('token', params.token)
        .single();

      if (tokenError || !tokenData) {
        log.warn('Cannot track view - token not found', {
          tokenPrefix: params.token.substring(0, 8)
        });
        return false;
      }

      const now = new Date().toISOString();
      const deviceType = detectDeviceType(params.userAgent || null);

      // Check if token is active and not expired before counting as successful view
      const isRevoked = tokenData.revoked_at || !tokenData.is_active;
      const isExpired = new Date(tokenData.expires_at) < new Date();
      const isValidToken = !isRevoked && !isExpired;

      // If token is invalid, still log the view event but don't increment counts
      if (!isValidToken && params.success !== false) {
        log.warn('Token is revoked or expired - logging failed view attempt', {
          tokenId: tokenData.id,
          isRevoked,
          isExpired
        });

        // Create view event for audit trail (failed attempt)
        await supabase
          .from('conditions_portal_view_events')
          .insert({
            token_id: tokenData.id,
            viewed_at: now,
            ip_address: params.ipAddress || null,
            user_agent: params.userAgent || null,
            device_type: deviceType,
            success: false,
            failure_reason: isRevoked ? 'Token revoked' : 'Token expired'
          });

        return false;
      }

      // Update token view stats
      const updateData: Record<string, unknown> = {
        last_viewed_at: now,
        view_count: (tokenData.view_count || 0) + 1,
        updated_at: now
      };

      // Set first_viewed_at if this is the first view
      if (!tokenData.first_viewed_at) {
        updateData.first_viewed_at = now;
      }

      const { error: updateError } = await supabase
        .from('conditions_portal_tokens')
        .update(updateData)
        .eq('id', tokenData.id);

      if (updateError) {
        log.error('Failed to update token view stats', {
          tokenId: tokenData.id,
          error: updateError.message
        });
        // Continue to create view event even if update failed
      }

      // Create view event for audit trail
      const { error: eventError } = await supabase
        .from('conditions_portal_view_events')
        .insert({
          token_id: tokenData.id,
          viewed_at: now,
          ip_address: params.ipAddress || null,
          user_agent: params.userAgent || null,
          device_type: deviceType,
          success: params.success !== false,
          failure_reason: params.failureReason || null
        });

      if (eventError) {
        log.error('Failed to create view event', {
          tokenId: tokenData.id,
          error: eventError.message
        });
        // Don't fail the entire operation for this
      }

      log.info('Portal view tracked successfully', {
        tokenId: tokenData.id,
        viewCount: (tokenData.view_count || 0) + 1,
        deviceType
      });

      return true;
    } catch (error) {
      log.error('Error tracking portal view', {
        tokenPrefix: params.token.substring(0, 8),
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }

  /**
   * Revoke a portal token manually
   * Used by loan officers to deactivate access
   */
  static async revokeToken(params: {
    tokenId: string;
    reason?: string;
    revokedByUserId?: string;
  }): Promise<boolean> {
    try {
      log.info('Revoking portal token', {
        tokenId: params.tokenId,
        reason: params.reason
      });

      const now = new Date().toISOString();

      const { error } = await supabase
        .from('conditions_portal_tokens')
        .update({
          is_active: false,
          revoked_at: now,
          revoked_reason: params.reason || 'Manual revocation',
          revoked_by_user_id: params.revokedByUserId || null,
          updated_at: now
        })
        .eq('id', params.tokenId);

      if (error) {
        log.error('Failed to revoke portal token', {
          tokenId: params.tokenId,
          error: error.message
        });
        return false;
      }

      log.info('Portal token revoked successfully', {
        tokenId: params.tokenId,
        reason: params.reason
      });

      return true;
    } catch (error) {
      log.error('Error revoking portal token', {
        tokenId: params.tokenId,
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }

  /**
   * Regenerate a portal token for a loan
   * Revokes existing tokens and creates a new one
   */
  static async regenerateToken(params: {
    encompassLoanId: string;
    loanNumber?: string;
    recipientEmail: string;
    recipientName: string;
    recipientType?: RecipientType;
    createdByUserId?: string;
    reason?: string;
  }): Promise<{ token: ConditionsPortalToken; portalUrl: string } | null> {
    try {
      log.info('Regenerating portal token', {
        encompassLoanId: params.encompassLoanId,
        recipientEmail: params.recipientEmail
      });

      // Revoke all existing active tokens for this loan/recipient
      await this.revokeTokensForLoanRecipient(
        params.encompassLoanId,
        params.recipientEmail,
        params.reason || 'Token regeneration'
      );

      // Create new token
      return this.createPortalToken({
        encompassLoanId: params.encompassLoanId,
        loanNumber: params.loanNumber,
        recipientEmail: params.recipientEmail,
        recipientName: params.recipientName,
        recipientType: params.recipientType,
        createdByUserId: params.createdByUserId
      });
    } catch (error) {
      log.error('Error regenerating portal token', {
        encompassLoanId: params.encompassLoanId,
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }

  /**
   * Revoke all tokens for a specific loan and recipient
   */
  private static async revokeTokensForLoanRecipient(
    encompassLoanId: string,
    recipientEmail: string,
    reason: string
  ): Promise<void> {
    const now = new Date().toISOString();

    const { error } = await supabase
      .from('conditions_portal_tokens')
      .update({
        is_active: false,
        revoked_at: now,
        revoked_reason: reason,
        updated_at: now
      })
      .eq('encompass_loan_id', encompassLoanId)
      .eq('recipient_email', recipientEmail.toLowerCase())
      .eq('is_active', true);

    if (error) {
      log.error('Failed to revoke tokens for loan/recipient', {
        encompassLoanId,
        recipientEmail,
        error: error.message
      });
    }
  }

  /**
   * Deactivate all tokens for a loan (called when loan is funded)
   * Uses the database function for efficiency
   */
  static async deactivateTokensForLoan(encompassLoanId: string, reason: string = 'Loan closed'): Promise<number> {
    try {
      log.info('Deactivating all tokens for loan', {
        encompassLoanId,
        reason
      });

      const { data, error } = await supabase.rpc('deactivate_conditions_portal_tokens_for_loan', {
        p_encompass_loan_id: encompassLoanId,
        p_reason: reason
      });

      if (error) {
        log.error('Failed to deactivate tokens for loan', {
          encompassLoanId,
          error: error.message
        });
        return 0;
      }

      const deactivatedCount = data || 0;

      log.info('Tokens deactivated for loan', {
        encompassLoanId,
        deactivatedCount
      });

      return deactivatedCount;
    } catch (error) {
      log.error('Error deactivating tokens for loan', {
        encompassLoanId,
        error: error instanceof Error ? error.message : String(error)
      });
      return 0;
    }
  }

  /**
   * Get all tokens for a loan (for admin/LO dashboard)
   */
  static async getTokensByLoan(encompassLoanId: string): Promise<ConditionsPortalToken[]> {
    try {
      log.debug('Fetching tokens for loan', { encompassLoanId });

      const { data, error } = await supabase
        .from('conditions_portal_tokens')
        .select('*')
        .eq('encompass_loan_id', encompassLoanId)
        .order('created_at', { ascending: false });

      if (error) {
        log.error('Failed to fetch tokens for loan', {
          encompassLoanId,
          error: error.message
        });
        return [];
      }

      const tokens = (data || []).map((row) => tokenRowToToken(row as ConditionsPortalTokenRow));

      log.info('Retrieved tokens for loan', {
        encompassLoanId,
        tokenCount: tokens.length,
        activeCount: tokens.filter((t) => t.isActive).length
      });

      return tokens;
    } catch (error) {
      log.error('Error fetching tokens for loan', {
        encompassLoanId,
        error: error instanceof Error ? error.message : String(error)
      });
      return [];
    }
  }

  /**
   * Get active token for a loan and recipient
   */
  static async getActiveToken(
    encompassLoanId: string,
    recipientEmail: string
  ): Promise<ConditionsPortalToken | null> {
    try {
      const now = new Date().toISOString();

      const { data, error } = await supabase
        .from('conditions_portal_tokens')
        .select('*')
        .eq('encompass_loan_id', encompassLoanId)
        .eq('recipient_email', recipientEmail.toLowerCase())
        .eq('is_active', true)
        .gt('expires_at', now)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !data) {
        return null;
      }

      return tokenRowToToken(data as ConditionsPortalTokenRow);
    } catch (error) {
      log.error('Error fetching active token', {
        encompassLoanId,
        recipientEmail,
        error: error instanceof Error ? error.message : String(error)
      });
      return null;
    }
  }

  /**
   * Get uploads for a token
   */
  static async getUploadsByToken(tokenId: string): Promise<ConditionsPortalUpload[]> {
    try {
      const { data, error } = await supabase
        .from('conditions_portal_uploads')
        .select('*')
        .eq('token_id', tokenId)
        .order('uploaded_at', { ascending: false });

      if (error) {
        log.error('Failed to fetch uploads for token', {
          tokenId,
          error: error.message
        });
        return [];
      }

      return (data || []).map((row) => uploadRowToUpload(row as ConditionsPortalUploadRow));
    } catch (error) {
      log.error('Error fetching uploads for token', {
        tokenId,
        error: error instanceof Error ? error.message : String(error)
      });
      return [];
    }
  }

  /**
   * Get view events for a token (audit trail)
   */
  static async getViewEvents(tokenId: string, limit: number = 100): Promise<ConditionsPortalViewEvent[]> {
    try {
      const { data, error } = await supabase
        .from('conditions_portal_view_events')
        .select('*')
        .eq('token_id', tokenId)
        .order('viewed_at', { ascending: false })
        .limit(limit);

      if (error) {
        log.error('Failed to fetch view events', {
          tokenId,
          error: error.message
        });
        return [];
      }

      return (data || []).map((row) => ({
        id: row.id,
        tokenId: row.token_id,
        viewedAt: row.viewed_at,
        ipAddress: row.ip_address,
        userAgent: row.user_agent,
        deviceType: row.device_type as DeviceType,
        success: row.success,
        failureReason: row.failure_reason
      }));
    } catch (error) {
      log.error('Error fetching view events', {
        tokenId,
        error: error instanceof Error ? error.message : String(error)
      });
      return [];
    }
  }

  /**
   * Check daily upload limit for a token
   * Returns true if under limit, false if exceeded
   */
  static async checkDailyUploadLimit(tokenId: string, maxPerDay: number = FILE_LIMITS.maxFilesPerDay): Promise<boolean> {
    try {
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      const { count, error } = await supabase
        .from('conditions_portal_uploads')
        .select('*', { count: 'exact', head: true })
        .eq('token_id', tokenId)
        .gte('uploaded_at', oneDayAgo);

      if (error) {
        log.error('Failed to check daily upload limit', {
          tokenId,
          error: error.message
        });
        return true; // Allow on error to not block user
      }

      const isUnderLimit = (count || 0) < maxPerDay;

      if (!isUnderLimit) {
        log.warn('Daily upload limit exceeded', {
          tokenId,
          count,
          maxPerDay
        });
      }

      return isUnderLimit;
    } catch (error) {
      log.error('Error checking daily upload limit', {
        tokenId,
        error: error instanceof Error ? error.message : String(error)
      });
      return true; // Allow on error
    }
  }

  /**
   * Check lifetime upload limit for a token
   */
  static async checkLifetimeUploadLimit(tokenId: string, maxLifetime: number = FILE_LIMITS.maxFilesPerTokenLifetime): Promise<boolean> {
    try {
      const { count, error } = await supabase
        .from('conditions_portal_uploads')
        .select('*', { count: 'exact', head: true })
        .eq('token_id', tokenId);

      if (error) {
        log.error('Failed to check lifetime upload limit', {
          tokenId,
          error: error.message
        });
        return true;
      }

      const isUnderLimit = (count || 0) < maxLifetime;

      if (!isUnderLimit) {
        log.warn('Lifetime upload limit exceeded', {
          tokenId,
          count,
          maxLifetime
        });
      }

      return isUnderLimit;
    } catch (error) {
      log.error('Error checking lifetime upload limit', {
        tokenId,
        error: error instanceof Error ? error.message : String(error)
      });
      return true;
    }
  }

  /**
   * Build the full portal URL for a token
   */
  static getPortalUrl(token: string): string {
    return `${PORTAL_BASE_URL}/${token}`;
  }
}

export default ConditionsPortalService;
